import { useEffect, useState } from 'react';
import type { PaymentType } from '../lib/api/payments';

export interface ApprovalFilters {
  types: PaymentType[];
  queues: Array<'urgent' | 'batch'>;
  needCeo: boolean | null;
}

export const EMPTY_FILTERS: ApprovalFilters = {
  types: [],
  queues: [],
  needCeo: null,
};

export function countActiveFilters(f: ApprovalFilters): number {
  return f.types.length + f.queues.length + (f.needCeo !== null ? 1 : 0);
}

interface Props {
  open: boolean;
  initial: ApprovalFilters;
  onClose: () => void;
  onApply: (f: ApprovalFilters) => void;
}

const TYPE_LABELS: Record<PaymentType, string> = {
  chi_thuong: 'Thanh toán',
  tam_ung: 'Tạm ứng',
  ung_luong: 'Ứng lương',
  hoan_ung: 'Hoàn ứng',
  thuong_nong: 'Thưởng nóng',
};

export function ApprovalFilterModal({ open, initial, onClose, onApply }: Props) {
  const [filters, setFilters] = useState<ApprovalFilters>(initial);

  useEffect(() => {
    if (open) setFilters(initial);
  }, [open, initial]);

  if (!open) return null;

  const toggleType = (t: PaymentType) => {
    setFilters((f) => ({
      ...f,
      types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t],
    }));
  };
  const toggleQueue = (q: 'urgent' | 'batch') => {
    setFilters((f) => ({
      ...f,
      queues: f.queues.includes(q) ? f.queues.filter((x) => x !== q) : [...f.queues, q],
    }));
  };
  const setCeo = (v: boolean | null) => setFilters((f) => ({ ...f, needCeo: v }));
  const reset = () => setFilters(EMPTY_FILTERS);
  const apply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <div className="afm-backdrop" onClick={onClose}>
      <div className="afm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="afm-head">
          <h3>Bộ lọc</h3>
          <button className="afm-close" onClick={onClose} title="Đóng">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="afm-body">
          {/* Loại đề nghị */}
          <div className="afm-group">
            <div className="afm-group-label">Loại đề nghị</div>
            {(['chi_thuong', 'tam_ung', 'hoan_ung', 'thuong_nong'] as PaymentType[]).map((t) => (
              <label key={t} className="afm-check">
                <input
                  type="checkbox"
                  checked={filters.types.includes(t)}
                  onChange={() => toggleType(t)}
                />
                <span>{TYPE_LABELS[t]}</span>
              </label>
            ))}
          </div>

          {/* Độ ưu tiên (queue) */}
          <div className="afm-group">
            <div className="afm-group-label">Độ ưu tiên</div>
            <label className="afm-check">
              <input
                type="checkbox"
                checked={filters.queues.includes('urgent')}
                onChange={() => toggleQueue('urgent')}
              />
              <span>🔥 Khẩn (duyệt trong ngày)</span>
            </label>
            <label className="afm-check">
              <input
                type="checkbox"
                checked={filters.queues.includes('batch')}
                onChange={() => toggleQueue('batch')}
              />
              <span>📅 Bình thường (batch T6)</span>
            </label>
          </div>

          {/* CEO */}
          <div className="afm-group">
            <div className="afm-group-label">Yêu cầu CEO ký</div>
            <label className="afm-check">
              <input
                type="radio"
                name="ceo"
                checked={filters.needCeo === null}
                onChange={() => setCeo(null)}
              />
              <span>Tất cả</span>
            </label>
            <label className="afm-check">
              <input
                type="radio"
                name="ceo"
                checked={filters.needCeo === true}
                onChange={() => setCeo(true)}
              />
              <span>Cần CEO ký (≥ 3.000.000đ)</span>
            </label>
            <label className="afm-check">
              <input
                type="radio"
                name="ceo"
                checked={filters.needCeo === false}
                onChange={() => setCeo(false)}
              />
              <span>KTT duyệt cuối (&lt; 3.000.000đ)</span>
            </label>
          </div>
        </div>

        <div className="afm-foot">
          <button className="afm-btn-ghost" onClick={reset}>Xoá bộ lọc</button>
          <div className="afm-foot-right">
            <button className="afm-btn-ghost" onClick={onClose}>Huỷ</button>
            <button className="afm-btn-primary" onClick={apply}>Áp dụng</button>
          </div>
        </div>
      </div>
    </div>
  );
}
